import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Text from '../../../../shared/_TBComponents/Text';

const ToolTipWrapper = styled('div')(({ x, y }) => ({
  position: 'absolute',
  pointerEvents: 'none',
  transform: `translate(${x}px, ${y}px)`,
}));

const ToolTipContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  max-width: 275px;
  max-height: 400px;
  background-color: #FFFFFF;
  border: solid 1px #EEEEEE;
  border-radius: 8px;
  padding: 8px 16px;
  font-family: 'Nunito Sans';
`;

const ContentWrapper = styled('div')`
  display: flex;
  flex-direction: ${({ bucketCount }) => (bucketCount > 5 ? 'row' : 'column')};
  flex-wrap: wrap;
  gap: 0 20px;
  overflow-y: auto;
`;

const CustomChartToolTip = ({
  active,
  payload,
  label,
  buckets,
  barSize,
  chartWidth,
  coordinate,
  showAssigned,
  hoveredBucketKey,
}) => {
  if (!active || !payload?.length || !coordinate) return null;
  if (!showAssigned && !hoveredBucketKey) return null;

  // ToolTip positioning logic
  const groupWidth = barSize * 2 * buckets.length + 10;
  const buffer = 320;
  const shouldFlipToolTipLeft = coordinate.x + groupWidth + buffer > chartWidth;
  const tooltipX = shouldFlipToolTipLeft
    ? coordinate.x - groupWidth / 2 - 125
    : coordinate.x + groupWidth / 2 + 8;
  const tooltipY = 30;

  const grouped = payload.reduce((acc, entry) => {
    const [type, range] = entry.dataKey.split('_');
    if (!acc[range]) acc[range] = {};
    acc[range][type] = {
      color: entry.stroke,
      value: (() => {
        const raw = entry.payload?.[`${entry.dataKey}_raw`] ?? entry.value ?? 0;
        return showAssigned ? Math.round(raw) : parseFloat(raw.toFixed(1));
      })(),
    };
    return acc;
  }, {});

  return (
    <>
      {showAssigned ? (
        <ToolTipWrapper x={tooltipX} y={tooltipY}>
          <ToolTipContainer>
            <Text styledTextProps={{ fontWeight: 700, color: '#444444', fontSize: 12 }}>
              {label}
            </Text>
            <ContentWrapper bucketCount={buckets.length}>
              {buckets.map(({ key, label: bucketLabel }) => {
                const assigned = grouped[key]?.assigned ?? {};
                const completed = grouped[key]?.completed ?? {};
                return (
                  <div key={key} style={{ marginTop: '10px' }}>
                    <Text
                      styledTextProps={{
                        fontWeight: 600,
                        color: completed.color || assigned.color || '#444444',
                        fontSize: 13,
                      }}
                    >
                      {bucketLabel}
                    </Text>
                    {showAssigned && (
                      <Text styledTextProps={{ fontSize: 12 }}>
                        Assigned:
                        {' '}
                        {assigned.value ?? 0}
                      </Text>
                    )}
                    <Text styledTextProps={{ fontSize: 12 }}>
                      {showAssigned ? 'Completed' : 'Reps Completed'}
                      :
                      {completed.value ?? 0}
                    </Text>
                  </div>
                );
              })}
            </ContentWrapper>
          </ToolTipContainer>
        </ToolTipWrapper>
      ) : (
        <ToolTipContainer>
          <Text styledTextProps={{ fontWeight: 700, color: '#444444', fontSize: 12 }}>
            {label}
          </Text>
          {(() => {
            if (!showAssigned && !hoveredBucketKey) return null;

            const hovered = payload.find((p) => p.dataKey === `completed_${hoveredBucketKey}`);
            if (!hovered) return null;

            const [, key] = hovered.dataKey.split('_');
            const color = hovered.stroke ?? '#444';
            const reps = hovered.payload?.[`${hovered.dataKey}_raw`] ?? hovered.value ?? 0;
            const tonnage = hovered.payload?.[`tonnage_${key}`] ?? 0;

            return (
              <div style={{ marginTop: '10px' }}>
                <Text styledTextProps={{ fontWeight: 600, color, fontSize: 13 }}>
                  {buckets.find((b) => b.key === key)?.label || key}
                </Text>
                <Text styledTextProps={{ fontSize: 12 }}>
                  Reps:
                  {' '}
                  {reps}
                </Text>
                <Text styledTextProps={{ fontSize: 12 }}>
                  Tonnage:
                  {' '}
                  {Math.round(tonnage)}
                </Text>
              </div>
            );
          })()}
        </ToolTipContainer>
      )}
    </>
  );
};

CustomChartToolTip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number,
    payload: PropTypes.shape({
      assigned: PropTypes.number,
      completed: PropTypes.number,
    }),
  })),
  label: PropTypes.string,
  buckets: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })),
  barSize: PropTypes.number.isRequired,
  chartWidth: PropTypes.number.isRequired,
  coordinate: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  showAssigned: PropTypes.bool,
  hoveredBucketKey: PropTypes.string,
};

CustomChartToolTip.defaultProps = {
  active: false,
  payload: [],
  label: '',
  buckets: [],
  showAssigned: false,
  hoveredBucketKey: '',
};

export default CustomChartToolTip;
